/* eslint-disable react/require-default-props */
/* eslint-disable no-nested-ternary */
import { StyledTableCell, StyledTableRow } from 'ui/StyledTable';
import { DateTime } from 'luxon';
import { useLocation, useNavigate } from 'react-router';
import { Box, Button, Checkbox, Chip, Tooltip, Typography } from '@mui/material';
import { CheckBoxOutlineBlank } from '@mui/icons-material';
import { nanoid } from '@reduxjs/toolkit';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { ItemWithId } from 'services/constants/types';
import { ETableStructureType } from 'services/constants/tableStructureTypes';
import { UserAvatar } from 'ui/UserAvatar';
import { TEasRowProps } from './types';

export const EasRow = <T extends ItemWithId>({
	item,
	setSelectedRow,
	index,
	selectedRow,
	tableStructure,
	handleAction,
	showButtons,
	checkedRow = false,
	handleLink,
	highlightedRow,
	setCheckRow,
	CustomComponent,
}: TEasRowProps<T>) => {
	const { search } = useLocation();
	const navigate = useNavigate();

	const handleLinkClick = () => {
		const linkSelector = tableStructure.find((i) => i.type === ETableStructureType.LINK)?.selector;
		if (linkSelector) {
			const id = item?.[linkSelector];
			if (handleLink) {
				handleLink(id as number);
			} else navigate(String(id));
		}
	};

	const handleActionStopPropagation = (
		event: React.MouseEvent<HTMLButtonElement>,
		id: number,
		type: string,
	) => {
		event.stopPropagation();
		handleAction(id, type);
	};

	return (
		<StyledTableRow
			key={nanoid()}
			sx={{
				'&:last-child td, &:last-child th': { border: 0 },
				background: checkedRow
					? '#03629338!important'
					: highlightedRow === index
						? '#0362937a!important'
						: selectedRow === index
							? '#03629338!important'
							: 'auto',
				p: '0px',
				m: '0px',
			}}
			onClick={handleLinkClick}
			onMouseOver={() => setSelectedRow(index)}
		>
			{tableStructure?.map((filter) => {
				if (filter.show === false) return null;
				return (
					<StyledTableCell
						key={nanoid()}
						align={filter?.align ?? 'center'}
						colSpan={filter?.colSpan}
						sx={{
							padding: '4px 8px 4px 8px',
							verticalAlign: 'middle',
							lineHeight: '1rem',
							maxWidth: '250px',
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							position: 'relative',
							p: '4px!important',
							m: '0px',
						}}
					>
						{filter?.type === ETableStructureType.CHECKBOX ? (
							<Checkbox
								checked={checkedRow}
								sx={{ color: 'primary.main', m: 0, p: 0, pl: 1 }}
								onChange={(e) => {
									setCheckRow?.(e.target.checked);
									e.stopPropagation();
								}}
								onClick={(e) => {
									e.stopPropagation();
								}}
								// inputProps={{ 'aria-label': 'primary checkbox' }}
							/>
						) : filter?.type === ETableStructureType.COUNTRY_CODE ||
						  filter?.type === ETableStructureType.COUNTRY_NAME ? (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									justifyContent: 'start',
									width: '100%',
									// gap: '5px',
								}}
							>
								<CountryWithFlag
									countryCode={
										filter?.type === ETableStructureType.COUNTRY_CODE
											? (item?.[filter.selector] as string)
											: undefined
									}
									countryName={
										filter?.type === ETableStructureType.COUNTRY_NAME
											? (item?.[filter.selector] as string)
											: undefined
									}
									size="small"
								/>
							</Box>
						) : filter?.type === ETableStructureType.DATE ? (
							item?.[filter.selector] !== null ? (
								<Chip
									label={DateTime.fromISO(String(item?.[filter.selector])).toFormat('yyyy-MM-dd')}
									sx={{
										backgroundColor: 'primary.light',
										fontSize: '0.6rem',
										borderRadius: '12px',
										opacity: 0.8,
										height: '20px',
										'& .MuiChip-label': {
											padding: '0px 8px',
											fontSize: '0.7rem',
											opacity: 0.8,
										},
									}}
								/>
							) : null
						) : filter?.type === ETableStructureType.DATE_PERIOD ? (
							<Chip
								label={String(item?.[filter.selector])
									?.split(' - ')
									?.map((date, i) => (
										<Typography key={nanoid()} variant="caption">
											{DateTime.fromISO(date).toFormat('yyyy-MM-dd')}
											{i === 0 ? ' - ' : ''}
										</Typography>
									))}
								sx={{
									backgroundColor: 'primary.light',
									fontSize: '0.6rem',
									borderRadius: '12px',
									opacity: 0.8,
									height: '20px',
									'& .MuiChip-label': {
										padding: '0px 8px',
										fontSize: '0.7rem',
										opacity: 0.7,
									},
								}}
							/>
						) : filter.type === ETableStructureType.AMOUNT ? (
							<AmountAndCurrency
								size={0.75}
								amount={item?.[filter.selector] === '' ? 0 : Number(item?.[filter.selector] ?? 0)}
								currency={
									typeof filter?.currency === 'function'
										? filter?.currency(item)
										: (filter?.currency ?? 'EUR')
								}
								bold
								color="primary"
							/>
						) : filter?.type === ETableStructureType.USER ? (
							item?.[filter.selector] !== null ? (
								<UserAvatar
									user={(item?.[filter.selector] as { fullName: string })?.fullName}
									size="xxs"
									onClick={() => {}}
								/>
							) : null
						) : filter?.type === ETableStructureType.STATUS && !!item?.[filter.selector] ? (
							<Chip
								label={String(item?.[filter.selector] ?? '')}
								sx={{
									backgroundColor:
										filter?.colors?.find((color) => color[0] === item?.[filter.selector])?.[1] ??
										'primary.light',
									fontSize: '0.6rem',
									borderRadius: '12px',
									height: '20px',
									'& .MuiChip-label': {
										opacity: 0.6,
										padding: '0px 8px',
										fontSize: '0.7rem',
									},
								}}
							/>
						) : filter?.type === ETableStructureType.LINK ? (
							<Typography
								component="span"
								sx={{
									color: 'primary.main',
									fontSize: '0.75rem',
									textDecoration: 'none',
									fontWeight: 'bold',
									cursor: 'pointer',
								}}
							>
								{item?.[filter.selector] !== null ? (item?.[filter.selector] as string) : ''}
							</Typography>
						) : filter?.type === ETableStructureType.TEXT ? (
							<Tooltip title={item?.[filter.selector] as string}>
								<Typography
									sx={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										white: 'nowrap',
										maxWidth: '200px',
										fontSize: '0.7rem',
										color: 'primary.main',
									}}
								>
									{item?.[filter.selector] as string}
								</Typography>
							</Tooltip>
						) : filter?.type === ETableStructureType.BOOLEAN ? (
							<CheckBoxOutlineBlank />
						) : filter?.type === ETableStructureType.BUTTONS ? (
							<Box
								key={nanoid()}
								sx={{
									display: 'inline-flex',
									flexDirection: 'row',
									gap: '8px',
								}}
							>
								{(filter.buttonFilter ? filter.buttonFilter(item) : filter.buttons)?.map(
									(button) => (
										<Button
											key={nanoid()}
											color="primary"
											variant={selectedRow === index ? 'contained' : 'outlined'}
											// variant="contained"
											size="small"
											sx={{
												borderRadius: '12px',
												textTransform: 'none',
												height: '24px',
												padding: '4px 6px!important',
												margin: '0px',
												border: '0.5px solid #016193!important',
												fontSize: '0.7rem',
											}}
											onClick={(event) => handleActionStopPropagation(event, item.id, button)}
										>
											{button}
										</Button>
									),
								)}
							</Box>
						) : filter?.type === ETableStructureType.CUSTOM ? (
							CustomComponent ? (
								<CustomComponent item={item} />
							) : null
						) : typeof item?.[filter?.selector] === 'string' ||
						  typeof item?.[filter?.selector] === 'number' ? (
							String(item?.[filter?.selector])
						) : (
							String(item?.[filter?.selector] ?? '')
						)}
					</StyledTableCell>
				);
			})}
		</StyledTableRow>
	);
};
